// OperationReport44Component.jsx
import React, { useEffect, useState } from "react";
import { Container, Row, Col, Label, Table } from "reactstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import DatePicker, { registerLocale } from "react-datepicker";
import { format, parseISO } from "date-fns";
import th from "date-fns/locale/th";
import { th as thBuddhist } from "date-fns/esm/locale";
import { addYears } from "date-fns/esm/fp";
import getYear from "date-fns/getYear";
import getMonth from "date-fns/getMonth";
import { GetVMdDept } from "../../share/api/MasterDataApi";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

/* ===== locales ===== */
registerLocale("th-buddhist", {
  ...th,
  options: { ...th.options, weekStartsOn: 1 },
  formatLong: thBuddhist.formatLong,
});

const thaiMonths = [
  "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
  "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"
];
const range = (start, end) => new Array(end - start).fill().map((_, i) => i + start);
const years = range(1963, new Date().getFullYear() + 8);

/* ===== helpers ===== */
const groupBy = (arr, key) =>
  arr.reduce((a, it) => {
    const k = (it[key] ?? "").toString();
    (a[k] ??= []).push(it);
    return a;
  }, {});
const isUniform = (rows, field) =>
  rows.every(r => (r[field] ?? "") === (rows[0]?.[field] ?? ""));

const N = v => Number(v ?? 0);
function fmtMoney(n) {
  const num = Number(String(n ?? 0).replace(/,/g, ""));
  return num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}
function toThaiDateLabel(v) {
  if (!v) return "-";
  if (typeof v === "string" && /\s[ก-๙]/.test(v)) return v;
  const d = typeof v === "string" ? parseISO(v) : v;
  const be = addYears(543, d);
  const monthName = format(be, "MMMM", { locale: th });
  return `${format(be, "dd")} ${monthName} ${format(be, "yyyy")}`;
}
function formatMonthYearBE(date) {
  if (!date) return "";
  const be = addYears(543, date);
  return format(be, "MMMM yyyy", { locale: th });
}
function pick(...c) {
  for (const x of c) {
    if (x !== undefined && x !== null && String(x).trim() !== "") return x;
  }
  return "";
}

/** คำนวณ rowSpan เพื่อรวมเซลล์เฉพาะช่วงที่ “ค่าซ้ำติดกัน” */
function calcRowSpans(rows, field) {
  const spans = Array(rows.length).fill(0);
  let i = 0;
  while (i < rows.length) {
    const v = rows[i]?.[field] ?? "";
    let j = i + 1;
    while (j < rows.length && (rows[j]?.[field] ?? "") === v) j++;
    spans[i] = j - i; // แถวแรกของบล็อกได้ rowspan, แถวถัดไปไม่ render คอลัมน์นั้น
    i = j;
  }
  return spans;
}

/* ===== PDF Thai font (optional) ===== */
function base64FromArrayBuffer(buf) {
  const b = new Uint8Array(buf);
  const c = 0x8000;
  let s = "";
  for (let i = 0; i < b.length; i += c) {
    s += String.fromCharCode.apply(null, b.subarray(i, i + c));
  }
  return btoa(s);
}
let thaiFontLoaded = false;
async function ensureThaiFontLoaded() {
  if (thaiFontLoaded) return;
  const [regRes, boldRes] = await Promise.all([
    fetch("/fonts/Sarabun-Regular.ttf"),
    fetch("/fonts/Sarabun-Bold.ttf")
  ]);
  const [regBuf, boldBuf] = await Promise.all([regRes.arrayBuffer(), boldRes.arrayBuffer()]);
  const regB64 = base64FromArrayBuffer(regBuf);
  const boldB64 = base64FromArrayBuffer(boldBuf);
  jsPDF.API.events.push(['addFonts', function () {
    this.addFileToVFS("Sarabun-Regular.ttf", regB64);
    this.addFileToVFS("Sarabun-Bold.ttf", boldB64);
    this.addFont("Sarabun-Regular.ttf", "Sarabun", "normal");
    this.addFont("Sarabun-Bold.ttf", "Sarabun", "bold");
  }]);
  new jsPDF();
  thaiFontLoaded = true;
}

/* ===== Component ===== */
export function OperationReport44Component() {
  const [reportMonth, setReportMonth] = useState("");
  const [reportMonthDate, setReportMonthDate] = useState(null);
  const user = JSON.parse(localStorage.getItem("userData") || "{}");
  const roleId = String(user?.roleId ?? user?.roldId ?? "");
  const userDeptId = String(user?.deptId ?? "all");

  const [deptsData, setDeptsData] = useState([]);
  const [deptOptions, setDeptOptions] = useState([]);
  const [deptIdFilter, setDeptIdFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const totalPayment = items.reduce((s, r) => s + N(r.PaymentAmount), 0);

  // จะโชว์คอลัมน์ "หน่วยงานที่อนุมัติคำขอ" เมื่อเงื่อนไขนี้เป็นจริงเท่านั้น
  const showDeptCol = (deptIdFilter === "all" && roleId === "4");

  useEffect(() => { GetVMdDept({ setDeptsData, setLoading }); }, []);
  useEffect(() => {
    const mapped = (deptsData || []).map(d => ({
      value: String(d?.value ?? d?.deptId ?? d?.DeptId ?? d?.id ?? ""),
      label: String(d?.label ?? d?.deptName ?? d?.DeptName ?? d?.name ?? "")
    }));
    if (roleId === "4") {
      setDeptOptions([{ value: "all", label: "ทุกหน่วยงาน" }, ...mapped]);
      setDeptIdFilter(p => p ?? "all");
    } else if (roleId === "3") {
      const self = mapped.find(o => o.value === userDeptId) || { value: userDeptId, label: "หน่วยงานของฉัน" };
      setDeptOptions([self]);
      setDeptIdFilter(userDeptId);
    } else {
      setDeptOptions(mapped);
      setDeptIdFilter(p => p || (mapped.find(o => o.value === userDeptId)?.value ?? ""));
    }
  }, [deptsData, roleId, userDeptId]);

  useEffect(() => {
    if (!reportMonth) { setReportMonthDate(null); return; }
    const [y, m] = reportMonth.split("-").map(Number);
    setReportMonthDate(new Date(y, m - 1, 1));
  }, [reportMonth]);

  // โหลดข้อมูลจาก API (SupportOperation44ViewModel)
  const checkAndLoadMonth = async () => {
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("monthYear", reportMonth === "" ? "all" : reportMonth);
      fd.append("deptIdFilter", deptIdFilter || "all");

      const res = await fetch("/api/SupportOperation/listSupportOperation44", {
        method: "POST",
        headers: { token: user.token },
        body: fd
      });
      const j = await res.json();
      let raw = Array.isArray(j?.dataenum) ? j.dataenum : [];

      // ถ้า role ระดับกรม + เลือกทุกหน่วยงาน -> กรองเฉพาะที่อนุมัติแล้ว (ถ้ามีฟิลด์บอก)
      if (roleId === "4" && (deptIdFilter === "all" || !deptIdFilter)) {
        const approveKeys = ["approveDeptName", "ApproveDeptName", "approveDept", "ApproveDept", "deptApproved", "DeptApproved", "deptName", "DeptName"];
        const hasApproveField = raw.some(x => approveKeys.some(k => k in (x || {})));
        if (hasApproveField) {
          raw = raw.filter(x => approveKeys.some(k => !!(x?.[k])));
        }
      }

      const mapped = raw.map(x => {
        const SupportNo = pick(x.SupportNo, x.supportNo);
        const SupportName = pick(x.SupportName, x.supportName);

        // 🔸 ใช้สองฟิลด์นี้เป็น "สาขา" และ "ระดับ" ตามที่ผู้ใช้ระบุ
        const TestOccName = pick(x.TestOccupationName, x.testOccupationName); // สาขา
        const Level = pick(x.Level, x.level);                                // ระดับ

        const PayStart = pick(x.PayRateStartDate, x.payRateStartDate);
        const PayEnd = pick(x.PayRateEndDate, x.payRateEndDate);
        const PayRateAmount = pick(x.PayRateAmount, x.payRateAmount);
        const PaymentAmount = pick(x.PaymentAmount, x.paymentAmount);
        const PaymentDate = pick(x.PaymentDate, x.paymentDate);

        // 🔹 หน่วยงานที่อนุมัติคำขอ (จาก API: คาดว่าใช้ deptName/DeptName หรือ approveDeptName)
        const ApproveDeptName = pick(
          x.deptName, x.DeptName,
          x.approveDeptName, x.ApproveDeptName,
          x.approveDept, x.ApproveDept
        );

        return {
          SupportNo,
          SupportName,
          ApproveDeptName, // ใช้แสดงคอลัมน์ใหม่เมื่อ showDeptCol
          TestOccName,
          Level,
          PayStart,
          PayEnd,
          PayRateAmount,
          PaymentAmount: N(String(PaymentAmount ?? 0).replace(/,/g, "")),
          PaymentDate
        };
      });

      setItems(mapped);
    } catch (e) {
      Swal.fire("โหลดข้อมูลไม่สำเร็จ", "", "error");
      setItems([]);
    } finally { setLoading(false); }
  };



  // ===== Export helpers =====
  const buildExportHeaders = () => {
    const base = [
      "ที่",
      "เลขที่คำขอ",
      "ชื่อผู้ขอรับเงินช่วยเหลือหรืออุดหนุน",
    ];
    if (showDeptCol) base.push("หน่วยงานที่อนุมัติคำขอ");
    base.push(
      "ปี พ.ศ.",
      "จำนวน (คน)",
      "สาขา",
      "ระดับ",
      "จำนวนอัตราค่าจ้าง (บาท)",
      "ตั้งแต่วันที่",
      "ถึงวันที่",
      "จำนวนเงินช่วยเหลือหรืออุดหนุน (บาท)",
      "วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน"
    );
    return base;
  };

  const buildExportRows = () => {
    // Flatten the grouped rows for export (no rowSpan in files)
    let rows = [];
    let no = 1;
    Object.entries(groupBy(items, "SupportNo")).forEach(([sn, groupRows]) => {
      groupRows.forEach((r, idx) => {
        const rec = [
          idx === 0 ? no : "",
          r.SupportNo || "-",
          r.SupportName || "-",
        ];
        if (showDeptCol) rec.push(r.ApproveDeptName || "-");
        rec.push(
          "-", // ปี พ.ศ. (ไม่ได้ใช้ข้อมูลจริงในตารางนี้)
          "-", // จำนวน(คน)
          r.TestOccName || "-",
          r.Level || "-",
          r?.PayRateAmount ?? "-",
          toThaiDateLabel(r.PayStart),
          toThaiDateLabel(r.PayEnd),
          fmtMoney(r.PaymentAmount),
          toThaiDateLabel(r.PaymentDate)
        );
        rows.push(rec);
      });
      no += 1;
    });
    return rows;
  };

  const handleExportExcel = () => {
    const headers = buildExportHeaders();
    const data = buildExportRows();
    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "รายงาน ชอ.4-4");
    const ym = reportMonth ? reportMonth.replace("-", "") : "all";
    XLSX.writeFile(wb, `แบบรายงานผลการพิจารณาอนุมัติการให้เงินช่วยเหลือหรืออุดหนุนจากกองทุนพัฒนาฝีมือแรงงาน ชอ4-4.xlsx`);
  };

  const handleExportPDF = async () => {
    await ensureThaiFontLoaded();
    const doc = new jsPDF({ orientation: 'l', unit: 'pt', format: 'a4' });
    doc.setFont('Sarabun', 'normal');

    const title = 'แบบรายงานผลการพิจารณาอนุมัติการให้เงินช่วยเหลือหรืออุดหนุนจากกองทุนพัฒนาฝีมือแรงงาน ชอ.4-4';
    doc.setFontSize(14);
    doc.text(title, 40, 40);

    const head = [buildExportHeaders()];
    const body = buildExportRows();

    doc.autoTable({
      head,
      body,
      startY: 60,
      styles: { font: 'Sarabun', fontSize: 9, cellPadding: 3, overflow: 'linebreak' },
      headStyles: { font: 'Sarabun', fontStyle: 'bold' },
      margin: { left: 24, right: 24 },
    });

    // สรุปยอดรวมด้านล่าง
    const finalY = doc.lastAutoTable.finalY || 60;
    doc.setFontSize(12);
    doc.text(`รวมจำนวนเงินช่วยเหลือ: ${fmtMoney(totalPayment)} บาท`, 40, finalY + 24);

    const ym = reportMonth ? reportMonth.replace('-', '') : 'all';
    doc.save(`แบบรายงานผลการพิจารณาอนุมัติการให้เงินช่วยเหลือหรืออุดหนุนจากกองทุนพัฒนาฝีมือแรงงาน ชอ4-4.pdf`);
  };

  /* ===== render ===== */
  return (
    <Container className="py-3">
      <Row className="g-3 align-items-end">
        <Col md={6}>
          <Label>หน่วยงานกรมพัฒนาฝีมือแรงงาน</Label>
          <Select
            options={deptOptions}
            value={deptOptions.find(o => o.value === (deptIdFilter || "all")) || null}
            onChange={opt => setDeptIdFilter(opt?.value ?? "all")}
            isDisabled={roleId === "3"}
            className="text-center input-color"
            styles={{
              control: (b) => ({ ...b, borderRadius: "20px" }),
              singleValue: (b) => ({ ...b, color: "#0A66C2" })
            }}
            placeholder="เลือกหน่วยงาน"
            noOptionsMessage={() => "ไม่พบหน่วยงาน"}
          />
        </Col>
        <Col md={6}>
          <Label>รอบรายงาน (เดือน/ปี)</Label>
          <DatePicker
            selected={reportMonthDate}
            onChange={(date) => {
              if (date instanceof Date && !isNaN(date)) {
                const y = date.getFullYear(); const m = String(date.getMonth() + 1).padStart(2, "0");
                setReportMonth(`${y}-${m}`);
                setReportMonthDate(new Date(y, Number(m) - 1, 1));
              } else {
                setReportMonth("");
                setReportMonthDate(null);
              }
            }}
            dateFormat="MMMM yyyy"
            showMonthYearPicker
            showFullMonthYearPicker
            locale="th-buddhist"
            placeholderText="เลือกเดือน/ปี (พ.ศ.)"
            value={formatMonthYearBE(reportMonthDate)}
            renderCustomHeader={({ date, changeYear, changeMonth, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
              <div className="d-flex justify-content-between align-items-center px-2 py-1" style={{ gap: 8 }}>
                <button type="button" className="btn btn-sm btn-light" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>‹</button>
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                  <select className="form-select form-select-sm" style={{ width: 120 }} value={thaiMonths[getMonth(date)]} onChange={({ target: { value } }) => changeMonth(thaiMonths.indexOf(value))}>
                    {thaiMonths.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <select className="form-select form-select-sm" style={{ width: 100 }} value={getYear(date)} onChange={({ target: { value } }) => changeYear(Number(value))}>
                    {years.map(y => <option key={y} value={y}>{y + 543}</option>)}
                  </select>
                </div>
                <button type="button" className="btn btn-sm btn-light" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>›</button>
              </div>
            )}
            customInput={
              <input
                className="form-control rounded-pill text-center input-color"
                readOnly
                value={formatMonthYearBE(reportMonthDate)}
                placeholder="เลือกเดือน/ปี (พ.ศ.)"
              />
            }
          />
        </Col>

        <Col md="auto" className="pt-4 d-flex justify-content-end gap-2">
          <button className="btn btn-primary" onClick={checkAndLoadMonth}>ตรวจสอบ/โหลดรายงาน</button>
          <button className="btn btn-outline-success" onClick={handleExportExcel}>ส่งออก Excel</button>
          <button className="btn btn-outline-danger" onClick={handleExportPDF}>ส่งออก PDF</button>
        </Col>
      </Row>

      <div className="mt-3">
        <Table bordered responsive>
          <thead className="table-light">
            <tr>
              <th rowSpan={3} className="text-center align-middle">ที่</th>
              <th rowSpan={3} className="text-center align-middle">เลขที่คำขอ</th>
              <th rowSpan={3} className="text-center align-middle">ชื่อผู้ขอรับเงินช่วยเหลือหรืออุดหนุน</th>
              {showDeptCol && (
                <th rowSpan={3} className="text-center align-middle">หน่วยงานที่อนุมัติคำขอ</th>
              )}
              <th colSpan={4} className="text-center align-middle">ส่งลูกจ้างเข้ารับการทดสอบและผ่านการทดสอบมาตรฐานฝีมือแรงงานแห่งชาติ</th>
              <th colSpan={3} className="text-center align-middle">จ่ายค่าจ้างตามมาตรฐานฝีมือไม่น้อยกว่า 180 วัน</th>
              <th rowSpan={3} className="text-center align-middle">จำนวนเงินช่วยเหลือหรืออุดหนุน (บาท)</th>
              <th rowSpan={3} className="text-center align-middle">วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน</th>
            </tr>
            <tr>
              <th rowSpan={2} className="text-center">ปี พ.ศ.</th>
              <th rowSpan={2} className="text-center">จำนวน (คน)</th>
              <th colSpan={2} className="text-center">ช่วงเวลาจ่ายค่าจ้างตามมาตรฐานฝีมือ</th>
              <th rowSpan={2} className="text-center">จำนวนอัตราค่าจ้าง (บาท)</th>
            </tr>
            <tr>
              <th className="text-center">สาขา</th>
              <th className="text-center">ระดับ</th>
              <th className="text-center">ตั้งแต่วันที่</th>
              <th className="text-center">ถึงวันที่</th>
            </tr>
          </thead>

          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={12 + (showDeptCol ? 1 : 0)} className="text-center">ยังไม่มีรายการ</td></tr>
            )}

            {Object.entries(groupBy(items, "SupportNo")).map(([sn, rows], gIdx) => {
              const span = rows.length;

              // รวม “สาขา” (ใช้ TestOccName) และ “ระดับ” (ใช้ Level) เฉพาะช่วงที่ค่าซ้ำติดกัน
              const branchSpans = calcRowSpans(rows, "TestOccName");
              const levelSpans = calcRowSpans(rows, "Level");

              const uStart = isUniform(rows, "PayStart");
              const uEnd = isUniform(rows, "PayEnd");
              const uRate = isUniform(rows, "PayRateAmount");
              const uAmt = isUniform(rows, "PaymentAmount");
              const uPayDt = isUniform(rows, "PaymentDate");
              const uDept = isUniform(rows, "ApproveDeptName");

              return rows.map((r, idx) => (
                <tr key={`${sn}-${idx}`}>
                  <td className="text-center">{idx === 0 ? gIdx + 1 : ""}</td>

                  {/* เลขที่คำขอ & ชื่อผู้ขอฯ รวมแนวตั้งทั้งกลุ่ม */}
                  {idx === 0 && <td className="text-center" rowSpan={span}>{r.SupportNo || "-"}</td>}
                  {idx === 0 && <td className="text-start" rowSpan={span}>{r.SupportName || "-"}</td>}

                  {/* หน่วยงานที่อนุมัติคำขอ (แสดงเฉพาะเมื่อ showDeptCol) */}
                  {showDeptCol && (
                    uDept
                      ? (idx === 0 ? <td className="text-start" rowSpan={span}>{r.ApproveDeptName || "-"}</td> : null)
                      : <td className="text-start">{r.ApproveDeptName || "-"}</td>
                  )}

                  {/* ปี พ.ศ. / จำนวน(คน) — เวลานี้ใช้ "-" เพราะเราเอาค่าจริงไปใช้เป็นสาขา/ระดับแล้ว */}
                  <td className="text-center">-</td>
                  <td className="text-center">-</td>

                  {/* สาขา: ใช้ TestOccName + รวมเฉพาะค่าซ้ำติดกัน */}
                  {branchSpans[idx] > 0 && (
                    <td className="text-start" rowSpan={branchSpans[idx]}>
                      {r.TestOccName || "-"}
                    </td>
                  )}

                  {/* ระดับ: ใช้ Level + รวมเฉพาะค่าซ้ำติดกัน */}
                  {levelSpans[idx] > 0 && (
                    <td className="text-start" rowSpan={levelSpans[idx]}>
                      {r.Level || "-"}
                    </td>
                  )}

                  {/* จำนวนอัตราค่าจ้าง */}
                  {isUniform(rows, "PayRateAmount")
                    ? (idx === 0 ? <td className="text-center" rowSpan={span}>{r?.PayRateAmount ?? "-"}</td> : null)
                    : <td className="text-center">{r?.PayRateAmount ?? "-"}</td>}

                  {/* ช่วงเวลาเริ่ม/สิ้นสุด */}
                  {isUniform(rows, "PayStart")
                    ? (idx === 0 ? <td className="text-center" rowSpan={span}>{toThaiDateLabel(r.PayStart)}</td> : null)
                    : <td className="text-center">{toThaiDateLabel(r.PayStart)}</td>}
                  {isUniform(rows, "PayEnd")
                    ? (idx === 0 ? <td className="text-center" rowSpan={span}>{toThaiDateLabel(r.PayEnd)}</td> : null)
                    : <td className="text-center">{toThaiDateLabel(r.PayEnd)}</td>}

                  {/* จำนวนเงินช่วยเหลือ */}
                  {uAmt
                    ? (idx === 0 ? <td className="text-end" rowSpan={span}>{fmtMoney(rows[0].PaymentAmount)}</td> : null)
                    : <td className="text-end">{fmtMoney(r.PaymentAmount)}</td>}

                  {/* วันที่จ่าย */}
                  {uPayDt
                    ? (idx === 0 ? <td className="text-center" rowSpan={span}>{toThaiDateLabel(rows[0].PaymentDate)}</td> : null)
                    : <td className="text-center">{toThaiDateLabel(r.PaymentDate)}</td>}
                </tr>
              ));
            })}
          </tbody>

          {items.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={10 + (showDeptCol ? 1 : 0)} className="text-end fw-bold">รวมจำนวนเงินช่วยเหลือ</td>
                <td className="text-end fw-bold">{fmtMoney(totalPayment)}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </Table>
      </div>

      {loading && (
        <div className="position-fixed bottom-0 end-0 p-3">
          <div className="badge bg-secondary">กำลังโหลด...</div>
        </div>
      )}
    </Container>
  );
}
